/// <amd-module name="Test/StackTraceParser/StackTraceParser" />
import { IFilePathData, IParsedStackTraceInfo, IStackTraceParserService } from "Test/StackTraceParser/Contracts";
import { Services, VssService } from "VSS/Platform/Context";

export interface ILineColumnInfo {
    lineNumber: number;
    columnNumber: number;
}

export interface IPathInfo {
    isPath: boolean;
    isFullPath: boolean;
}

export class StackTraceParserService extends VssService implements IStackTraceParserService {

    private result: IParsedStackTraceInfo[] = [];
    private static readonly winDrivePrefix = "[a-zA-Z]:";
    private static readonly winPathPrefix = "(" + StackTraceParserService.winDrivePrefix + "|\\.\\.?)";
    private static readonly winPathSeparatorClause = "(\\\\|\\/)";
    private static readonly winExcludedPathCharactersClause = "[^\\0<>\\?\\|\\/\\s!$`&*()\\[\\]+'\":;]";
    /** A regex that matches paths in the form c:\foo, .\foo, ..\foo, foo\bar */
    private static readonly winLocalLinkClause = "((" + StackTraceParserService.winPathPrefix
        + "|(" + StackTraceParserService.winExcludedPathCharactersClause
        + ")+)?(" + StackTraceParserService.winPathSeparatorClause + "("
        + StackTraceParserService.winExcludedPathCharactersClause + ")+)+)";

    // There are some limitation with these resx parser.
    // Can not parse if space is in first folder name 
    // "folder with space\someFolder\file.cs, line 45 column 25" but it will parse "someFolder\folder with space\file.cs, line 45 column 25"
    private static readonly lineAndColumnClause = [
        "(([\\S\\s]*)\", line ((\\d+)( column (\\d+))?))", // "(file path)", line 45 column 25
        "(([\\S\\s]*) on line ((\\d+)(, column (\\d+))?))", // (file path) on line 8, column 13
        "(([\\S\\s]*):line ((\\d+)(, column (\\d+))?))", // (file path):line 8, column 13
        "(([^:\\(\\)<>\'\"\\[\\]]*)(:(\\d+))?(:(\\d+))?)" // (file path):336, (file path):336:9
    ].join("|").replace(/ /g, "[${'\u00A0'} ]");

    // Changing any regex may effect this value, hence changes this as well if required.
    private winLineAndColumnMatchIndex = 12;

    // Each line and column clause have 6 groups (ie no. of expressions in round brackets)
    private lineAndColumnClauseGroupCount = 6;

    private static readonly linkPattern: RegExp = new RegExp(`${StackTraceParserService.winLocalLinkClause}(${StackTraceParserService.lineAndColumnClause})`);

    public parseStackTrace(stackTrace: string): IParsedStackTraceInfo[] {
        let stackTraceList = stackTrace.split("\n");
        return stackTraceList.map((stackTraceLine) => this.parseStackTraceLine(stackTraceLine));
    }

    private parseStackTraceLine(stackTraceLine: string): IParsedStackTraceInfo {
        // Trim initial white space 
        stackTraceLine = stackTraceLine.trim();

        let parsedStackTraceInfo: IParsedStackTraceInfo = {
            stackTrace: stackTraceLine,
        };

        let matches = StackTraceParserService.linkPattern.exec(stackTraceLine);

        // No path found or can not create URl from it.
        if (matches) {

            let pathInfo = this._checkIfFilePathCanCreateUrl(matches);
            if (pathInfo.isPath) {

                let lineColumnInfo = this._extractLineAndColumnInfo(matches);
                let filePath = this._extractfilePath(matches);

                let filePathdata: IFilePathData = {
                    filePath: filePath,
                    lineNumber: lineColumnInfo.lineNumber,
                    columnNumber: lineColumnInfo.columnNumber,
                    isFullPath: pathInfo.isFullPath
                };

                parsedStackTraceInfo.filePathData = filePathdata;
            }
        }

        return parsedStackTraceInfo;
    }

    // This function check if the parsed string is file path or not.
    // If string is file path then is it full path or not
    private _checkIfFilePathCanCreateUrl(matches: string[]): IPathInfo {
        let pathInfo: IPathInfo = {
            isPath: false,
            isFullPath: false
        };

        // if path is in the form "c:\file.txt", "..\file.txt", ".\file.txt" then
        // matches[3] will have "c:", ".." and "." repectively
        if (!matches[3]) {
            pathInfo.isPath = true;
            pathInfo.isFullPath = false;
            return pathInfo;
        }
        else {
            // check if path is in the form c:\foo, ..\foo
            let winPathprefix = "^(" + StackTraceParserService.winDrivePrefix + "|\\.\\.)";
            let resxPattern = new RegExp(winPathprefix);

            // matches[3] has the prefixes.
            let resxMatches = resxPattern.exec(matches[3]);

            // resxMatches[1] will get filled if matches[3] will be in the form "c:\foo" and "..\foo"
            if (resxMatches && resxMatches[1]) {
                pathInfo.isPath = true;
                pathInfo.isFullPath = true;
                return pathInfo;
            }
            else {
                if (matches[1]) {
                    // matches[1] is the path and it is in the form ".\foo"
                    matches[1] = matches[1].substring(matches[3].length);
                    pathInfo.isPath = true;
                    pathInfo.isFullPath = false;
                    return pathInfo;
                }
                else {
                    // The parsed string is not a path
                    return pathInfo;
                }
            }
        }
    }

    private _extractfilePath(matches: string[]): string {
        let fileFirstPart = matches[1];

        let lineAndColumnClauseLength = StackTraceParserService.lineAndColumnClause.length;
        for (let i = 0; i < lineAndColumnClauseLength; i++) {
            const lineMatchIndex = this.winLineAndColumnMatchIndex + (this.lineAndColumnClauseGroupCount * i);
            const fileSecondPart = matches[lineMatchIndex - 2];
            if (fileSecondPart) {
                return fileFirstPart + fileSecondPart;
            }
        }

        return fileFirstPart;
    }

    private _extractLineAndColumnInfo(matches: string[]): ILineColumnInfo {
        let lineColumnInfo: ILineColumnInfo = {
            lineNumber: 1,
            columnNumber: 1
        };

        if (matches == null) {
            return lineColumnInfo;
        }


        let lineAndColumnClauseLength = StackTraceParserService.lineAndColumnClause.length;
        for (let i = 0; i < lineAndColumnClauseLength; i++) {
            const lineMatchIndex = this.winLineAndColumnMatchIndex + (this.lineAndColumnClauseGroupCount * i);
            const rowNumber = matches[lineMatchIndex];
            if (rowNumber) {
                lineColumnInfo.lineNumber = parseInt(rowNumber, 10);

                // Check if column number exists
                const columnNumber = matches[lineMatchIndex + 2];
                if (columnNumber) {
                    lineColumnInfo.columnNumber = parseInt(columnNumber, 10);
                }
                break;
            }
        }

        return lineColumnInfo;
    }
}

Services.add("IStackTraceParserService", { serviceFactory: StackTraceParserService });
